import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePrivacy } from '../contexts/PrivacyContext';
import { apiService, Transaction, Insight, DashboardResponse } from '../services/api';
import { Button } from '../components/ui/button';
import { InsightsCarousel } from '../components/InsightsCarousel';
import {
  Sparkles,
  Send,
  TrendingUp,
  ArrowRight,
  ArrowUpRight,
  ArrowDownRight,
  ChevronRight,
  Wallet,
  CreditCard,
  Store
} from 'lucide-react';

// Time-based greeting with custom messages
const getGreeting = (): { text: string; emoji: string; subtext: string } => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) {
    return { text: 'Good morning', emoji: '☀️', subtext: 'Start your day with financial clarity' };
  }
  if (hour >= 12 && hour < 17) {
    return { text: 'Good afternoon', emoji: '🌤️', subtext: 'How are your finances looking today?' };
  }
  if (hour >= 17 && hour < 21) {
    return { text: 'Good evening', emoji: '🌅', subtext: 'Time to review your daily spending' };
  }
  return { text: 'Good night', emoji: '🌙', subtext: 'Your financial summary awaits' };
};

const suggestedQueries = [
  { text: "What were my biggest expenses?", icon: "💸" },
  { text: "How much did I spend on restaurant and meals?", icon: "🍔" },
  { text: "What's my spending trend?", icon: "📈" },
  { text: "How can I save more?", icon: "💰" }
];


export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { isPrivacyMode } = usePrivacy();
  const [inputMessage, setInputMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dashboardData, setDashboardData] = useState<DashboardResponse | null>(null);
  const [recentTransactions, setRecentTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [logoErrors, setLogoErrors] = useState<Set<string>>(new Set());
  
  // Insights state
  const [insights, setInsights] = useState<Insight[]>([]);
  const [insightsLoading, setInsightsLoading] = useState(true);

  useEffect(() => {
    const abortController = new AbortController();
    
    const fetchData = async () => {
      setLoading(true);
      setInsightsLoading(true);
      
      try {
        // Single optimized API call that replaces 3 separate calls
        const response = await apiService.getDashboard(abortController.signal);
        
        // Only update state if the request wasn't aborted
        if (!abortController.signal.aborted) {
          setDashboardData(response);
          setRecentTransactions(response.recent_transactions);
          setInsights(response.insights);
        }
      } catch (error) {
        // Ignore abort errors - they're expected when navigating away
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
        console.error('Failed to fetch dashboard:', error);
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
          setInsightsLoading(false);
        }
      }
    };

    fetchData();
    
    // Cleanup function - abort any in-flight requests when component unmounts
    return () => {
      abortController.abort();
    };
  }, []);


  // Calculate current and previous month totals using dashboard data
  const getMonthlyComparison = () => {
    if (!dashboardData) {
      return {
        currentExpenses: 0,
        previousExpenses: 0,
        currentIncome: 0,
        previousIncome: 0,
        expenseChange: 0,
        incomeChange: 0,
        expenseSparkline: [],
        incomeSparkline: [],
        netSavings: 0
      };
    }

    const currentExpenses = parseFloat(dashboardData.current_month.total_spending);
    const currentIncome = parseFloat(dashboardData.current_month.total_income);
    const expenseChange = dashboardData.current_month.spending_change_percent || 0;
    const incomeChange = dashboardData.current_month.income_change_percent || 0;

    return {
      currentExpenses,
      previousExpenses: 0, // Not needed with percent change from API
      currentIncome,
      previousIncome: 0, // Not needed with percent change from API
      expenseChange,
      incomeChange,
      expenseSparkline: [],
      incomeSparkline: [],
      netSavings: currentIncome - currentExpenses
    };
  };

  const handleInsightVote = useCallback(async (id: string, vote: 1 | -1) => {
    try {
      await apiService.voteInsight(id, vote);
      setInsights(prev => prev.filter(i => i.id !== id));
    } catch (err) {
      console.error('Failed to vote on insight:', err);
    }
  }, []);

  const handleSubmitQuery = async (query: string) => {
    if (!query.trim() || isSubmitting) return;
    
    setIsSubmitting(true);
    localStorage.setItem('alto_ai_initial_message', query.trim());
    navigate('/alto-ai');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitQuery(inputMessage);
    }
  };


  const formatCurrency = (value: number) => {
    if (isPrivacyMode) return '$••••';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatAmount = (amount: string) => {
    if (isPrivacyMode) return '$••••';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Math.abs(parseFloat(amount)));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const comparison = getMonthlyComparison();
  const greeting = getGreeting();
  const onboardingCompleted = !!profile?.onboarding_completed;

  return (
    <div
      className="overflow-x-hidden"
      style={{ backgroundColor: 'var(--theme-background)' }}
    >
      {/* Onboarding (shown until completed) */}
      {!onboardingCompleted && (
        <div className="px-4 pt-5 md:px-8 md:pt-7">
          <div
            className="apple-card cursor-pointer group fade-in-up overflow-hidden relative"
            onClick={() => navigate('/onboarding')}
            style={{ animationDelay: '50ms' }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') navigate('/onboarding');
            }}
          >
            <div
              className="absolute inset-0 opacity-[0.08] pointer-events-none"
              style={{
                background:
                  'radial-gradient(circle at top right, var(--theme-primary) 0%, transparent 55%)',
              }}
            />

            <div className="relative flex items-center gap-4">
              <div
                className="p-3 rounded-2xl"
                style={{
                  backgroundColor: 'color-mix(in srgb, var(--theme-primary) 12%, transparent)',
                }}
              >
                <Sparkles className="h-5 w-5" style={{ color: 'var(--theme-primary)' }} />
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                  Get your Alto Account setup
                </p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--theme-text-secondary)' }}>
                  Connect your bank, let Alto AI categorize, and unlock insights.
                </p>
              </div>

              <ChevronRight
                className="h-5 w-5 transition-transform group-hover:translate-x-0.5"
                style={{ color: 'var(--theme-text-secondary)' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Hero Header */}
      <div className="px-4 pt-6 pb-4 md:px-8 md:pt-8 md:pb-6">
        <div className="fade-in-up">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-2xl">{greeting.emoji}</span>
            <h1 
              className="text-2xl md:text-3xl font-bold tracking-tight"
              style={{ color: 'var(--theme-text)' }}
            >
              {greeting.text}, {profile?.first_name || 'there'}
            </h1>
          </div>
          <p 
            className="text-sm ml-9"
            style={{ color: 'var(--theme-text-secondary)' }}
          >
            {greeting.subtext}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 pb-6 md:px-8 md:pb-8">
        <div className="flex flex-col gap-6">
          
          {/* Quick Stats Row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              {/* Spending Card */}
              <div
                className="apple-card group cursor-pointer"
                onClick={() => navigate('/analytics')}
                style={{ animationDelay: '100ms' }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div
                    className="p-2 rounded-xl transition-transform duration-300 group-hover:scale-110"
                    style={{ backgroundColor: 'color-mix(in srgb, var(--theme-expense) 12%, transparent)' }}
                  >
                    <CreditCard className="h-4 w-4" style={{ color: 'var(--theme-expense)' }} />
                  </div>
                </div>
                <p
                  className="text-xl md:text-2xl font-bold tracking-tight"
                  style={{ color: 'var(--theme-text)' }}
                >
                  {loading ? '—' : formatCurrency(comparison.currentExpenses)}
                </p>
                <p
                  className="text-[10px] mt-0.5"
                  style={{ color: 'var(--theme-text-secondary)' }}
                >
                  Spending this month
                </p>
              </div>

              {/* Income Card */}
              <div
                className="apple-card group cursor-pointer"
                onClick={() => navigate('/analytics')}
                style={{ animationDelay: '150ms' }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div
                    className="p-2 rounded-xl transition-transform duration-300 group-hover:scale-110"
                    style={{ backgroundColor: 'color-mix(in srgb, var(--theme-income) 12%, transparent)' }}
                  >
                    <Wallet className="h-4 w-4" style={{ color: 'var(--theme-income)' }} />
                  </div>
                </div>
                <p
                  className="text-xl md:text-2xl font-bold tracking-tight"
                  style={{ color: 'var(--theme-text)' }}
                >
                  {loading ? '—' : formatCurrency(comparison.currentIncome)}
                </p>
                <p
                  className="text-[10px] mt-0.5"
                  style={{ color: 'var(--theme-text-secondary)' }}
                >
                  Income this month
                </p>
              </div>

              {/* Net Savings Card - Hidden on mobile, shown on larger screens */}
              <div
                className="apple-card hidden lg:block"
                style={{ animationDelay: '200ms' }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div
                    className="p-2 rounded-xl"
                    style={{
                      backgroundColor: comparison.netSavings >= 0
                        ? 'color-mix(in srgb, var(--theme-income) 12%, transparent)'
                        : 'color-mix(in srgb, var(--theme-expense) 12%, transparent)'
                    }}
                  >
                    <TrendingUp
                      className="h-4 w-4"
                      style={{
                        color: comparison.netSavings >= 0
                          ? 'var(--theme-income)'
                          : 'var(--theme-expense)'
                      }}
                    />
                  </div>
                </div>
                <p
                  className="text-xl md:text-2xl font-bold tracking-tight"
                  style={{ color: 'var(--theme-text)' }}
                >
                  {loading ? '—' : formatCurrency(comparison.netSavings)}
                </p>
                <p
                  className="text-[10px] mt-0.5"
                  style={{ color: 'var(--theme-text-secondary)' }}
                >
                  Net this month
                </p>
              </div>

              {/* Quick Action Card */}
              <div
                className="apple-card hidden lg:flex flex-col justify-between cursor-pointer group"
                onClick={() => navigate('/analytics')}
                style={{ animationDelay: '250ms' }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div
                    className="p-2 rounded-xl transition-transform duration-300 group-hover:scale-110"
                    style={{ backgroundColor: 'color-mix(in srgb, var(--theme-primary) 12%, transparent)' }}
                  >
                    <Sparkles className="h-4 w-4" style={{ color: 'var(--theme-primary)' }} />
                  </div>
                  <ChevronRight
                    className="h-4 w-4 group-hover:translate-x-1 transition-transform"
                    style={{ color: 'var(--theme-text-secondary)' }}
                  />
                </div>
                <div>
                  <p
                    className="text-sm font-semibold"
                    style={{ color: 'var(--theme-text)' }}
                  >
                    View Analytics
                  </p>
                  <p
                    className="text-[10px] mt-0.5"
                    style={{ color: 'var(--theme-text-secondary)' }}
                  >
                    Detailed breakdown
                  </p>
                </div>
              </div>
            </div>

          {/* AI Assistant Section */}
          <div
            className="apple-card fade-in-up overflow-hidden relative"
            style={{ animationDelay: '300ms' }}
          >
            {/* Decorative gradient background */}
            <div 
              className="absolute top-0 right-0 w-64 h-64 opacity-[0.05] pointer-events-none"
              style={{
                background: 'radial-gradient(circle, var(--theme-primary) 0%, transparent 70%)',
                transform: 'translate(30%, -30%)'
              }}
            />
            
            <div className="relative">
              {/* Header */}
              <div className="flex items-center gap-3 mb-5">
                <div 
                  className="p-3 rounded-2xl"
                  style={{
                    background: 'linear-gradient(135deg, var(--theme-primary) 0%, color-mix(in srgb, var(--theme-primary) 70%, #000) 100%)'
                  }}
                >
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 
                    className="text-lg font-bold"
                    style={{ color: 'var(--theme-text)' }}
                  >
                    Ask Alto AI
                  </h2>
                  <p 
                    className="text-xs"
                    style={{ color: 'var(--theme-text-secondary)' }}
                  >
                    Your personal finance assistant
                  </p>
                </div>
              </div>

              {/* Suggested Queries */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                {suggestedQueries.map((query, index) => (
                  <button
                    key={index}
                    onClick={() => handleSubmitQuery(query.text)}
                    className="group flex items-start gap-2 p-3 text-left rounded-xl
                             transition-all duration-200 hover:shadow-sm"
                    style={{ 
                      backgroundColor: 'color-mix(in srgb, var(--theme-text-secondary) 8%, transparent)',
                      border: '1px solid color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)'
                    }}
                  >
                    <span className="text-base flex-shrink-0">{query.icon}</span>
                    <span 
                      className="text-xs line-clamp-2 leading-relaxed transition-colors"
                      style={{ color: 'var(--theme-text-secondary)' }}
                    >
                      {query.text}
                    </span>
                  </button>
                ))}
              </div>

              {/* Input Field */}
              <div 
                className="relative rounded-2xl overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, color-mix(in srgb, var(--theme-primary) 15%, transparent), color-mix(in srgb, var(--theme-primary) 5%, transparent))',
                }}
              >
                <div 
                  className="flex items-center gap-3 p-3 m-[1px] rounded-2xl backdrop-blur-sm"
                  style={{ backgroundColor: 'color-mix(in srgb, var(--theme-surface) 90%, transparent)' }}
                >
                  <Sparkles 
                    className="h-5 w-5 flex-shrink-0" 
                    style={{ color: 'var(--theme-text-secondary)' }} 
                  />
                  <input
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask me anything about your finances..."
                    className="flex-1 bg-transparent border-none outline-none text-sm"
                    style={{ color: 'var(--theme-text)' }}
                    disabled={isSubmitting}
                  />
                  <button
                    onClick={() => handleSubmitQuery(inputMessage)}
                    disabled={!inputMessage.trim() || isSubmitting}
                    className="p-2.5 rounded-xl text-white transition-all duration-200
                             disabled:opacity-40 disabled:cursor-not-allowed
                             hover:opacity-90 active:scale-95"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* AI Insights */}
          <div className="fade-in-up" style={{ animationDelay: '350ms' }}>
            <InsightsCarousel
              insights={insights}
              onVote={handleInsightVote}
              onShowAll={() => navigate('/analytics')}
              loading={insightsLoading}
            />
          </div>

          {/* Recent Transactions - Only show when there are transactions */}
          {!loading && recentTransactions.length > 0 && (
            <div className="fade-in-up" style={{ animationDelay: '400ms' }}>
              <div className="flex items-center justify-between mb-3">
                <h2
                  className="text-base font-semibold"
                  style={{ color: 'var(--theme-text)' }}
                >
                  Recent Transactions
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/transactions')}
                  className="text-xs gap-1 -mr-2 hover:bg-transparent"
                  style={{ color: 'var(--theme-text-secondary)' }}
                >
                  View All
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>

              <div className="apple-card !p-0 overflow-hidden">
                <div>
                  {recentTransactions.map((transaction, index) => {
                    const isIncome = transaction.transaction_type === 'income';
                    const isTransfer = transaction.transaction_type === 'transfer';
                    const hasLogo = transaction.merchant_logo_url && !logoErrors.has(transaction.id);
                    const isLast = index === recentTransactions.length - 1;

                    return (
                      <div
                        key={transaction.id}
                        className="flex items-center gap-3 px-4 py-3.5 transition-colors cursor-pointer"
                        style={{
                          borderBottom: !isLast
                            ? '1px solid color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)'
                            : 'none'
                        }}
                        onClick={() => navigate('/transactions')}
                      >
                        {/* Merchant Logo / Icon */}
                        <div className="relative flex-shrink-0">
                          {hasLogo ? (
                            <img
                              src={transaction.merchant_logo_url || undefined}
                              alt={`${transaction.merchant_name} logo`}
                              className="h-10 w-10 rounded-xl object-cover"
                              onError={() => setLogoErrors(prev => new Set(prev).add(transaction.id))}
                            />
                          ) : (
                            <div
                              className="h-10 w-10 rounded-xl flex items-center justify-center"
                              style={{ backgroundColor: 'color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)' }}
                            >
                              <Store
                                className="h-5 w-5"
                                style={{ color: 'var(--theme-text-secondary)' }}
                              />
                            </div>
                          )}
                          {/* Type indicator dot */}
                          <div
                            className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center"
                            style={{
                              backgroundColor: isTransfer
                                ? 'var(--theme-accent)'
                                : isIncome
                                  ? 'var(--theme-income)'
                                  : 'var(--theme-expense)',
                              borderColor: 'var(--theme-surface)'
                            }}
                          >
                            {isTransfer ? (
                              <ArrowRight className="h-2 w-2 text-white" />
                            ) : isIncome ? (
                              <ArrowUpRight className="h-2 w-2 text-white" />
                            ) : (
                              <ArrowDownRight className="h-2 w-2 text-white" />
                            )}
                          </div>
                        </div>

                        {/* Transaction Details */}
                        <div className="flex-1 min-w-0">
                          <p
                            className="text-sm font-medium truncate"
                            style={{ color: 'var(--theme-text)' }}
                          >
                            {transaction.merchant_name}
                          </p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span
                              className="text-xs"
                              style={{ color: 'var(--theme-text-secondary)' }}
                            >
                              {formatDate(transaction.transaction_date)}
                            </span>
                            {!isTransfer && (
                              <>
                                <span style={{ color: 'var(--theme-text-secondary)', opacity: 0.5 }}>•</span>
                                <span
                                  className="text-xs truncate"
                                  style={{ color: 'var(--theme-text-secondary)', opacity: 0.7 }}
                                >
                                  {transaction.category}
                                </span>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Amount */}
                        <div className="text-right flex-shrink-0">
                          <span
                            className="text-sm font-semibold"
                            style={{
                              color: isTransfer
                                ? 'var(--theme-accent)'
                                : isIncome
                                  ? 'var(--theme-income)'
                                  : 'var(--theme-text)'
                            }}
                          >
                            {isTransfer ? '' : (isIncome ? '+' : '-')}{formatAmount(transaction.amount)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
    </div>
  );
};
